import { cx } from '@linaria/core';
import { Fragment } from 'react';
import { colors } from '@ui/kit/design-tokens';
import { RoundedCheckmarkIcon, PartialCheckmarkIcon, CrossIcon } from '@ui/kit/icons';
import { canValidateOpgavekompleksetTask } from '@utils/component-framework';
import { useOpgavekompleksetComponent } from '../../state/context.data.js';
import { useOpgavekompleksetEntry } from '../../state/hook.entry.js';
import { shouldShowReport } from '../../state/util.should-show-report.js';
import { wrapper, tokens, icon, getPointOnCircle, separator, text, chart } from './_pie-chart.style.js';
import { PieChartLegend } from './atom.legend.js';
import { jsx, jsxs } from 'react/jsx-runtime';

function ResultsPieChart(props) {
  const component = useOpgavekompleksetComponent();
  const taskEntries = useOpgavekompleksetEntry({
    select: entry => Object.values(entry?.data.tasks ?? {}).filter(taskEntry => {
      const relatedTask = component.data.tasks.find(task => task.id === taskEntry.id);
      if (!relatedTask) {
        return false;
      }

      // When the donut simply shows answered tasks, then all tasks, even
      // unvalidatable should be included.
      return !shouldShowReport(component.data.settings.flowType) ?
      // NB: We omit infopages from the results, See ADO #114226
      relatedTask.type !== "task" || relatedTask.taskType !== "InfoPage" : canValidateOpgavekompleksetTask(relatedTask);
    })
  }) ?? [];
  const hasFinsihedEntry = useOpgavekompleksetEntry({
    select: entry => !entry ? false : entry.envelope.result?.status === "completed"
  });
  const showResult = !!hasFinsihedEntry && shouldShowReport(component.data.settings.flowType);
  const counts = taskEntries.reduce((acc, curr) => ({
    notAnswered: acc.notAnswered + (curr.state.skipped ? 1 : 0),
    correct: acc.correct + (!curr.state.skipped && curr.stats.total > 0 && curr.stats.correct === curr.stats.total ? 1 : 0),
    wrong: acc.wrong + (!curr.state.skipped && curr.stats.wrong === curr.stats.total ? 1 : 0),
    partial: acc.partial + (!curr.state.skipped && curr.stats.correct !== curr.stats.total && curr.stats.wrong !== curr.stats.total ? 1 : 0)
  }), {
    notAnswered: 0,
    correct: 0,
    wrong: 0,
    partial: 0
  });
  const total = counts.correct + counts.notAnswered + counts.partial + counts.wrong;
  if (total === 0) {
    return null;
  }
  const segments = (() => {
    const base = [];
    if (!showResult) {
      const answered = total - counts.notAnswered;
      if (answered) {
        base.push({
          id: "answered",
          label: "besvaret",
          legend: "Besvaret",
          color: colors.gray.sim100(),
          offset: 0,
          count: answered,
          size: answered / total
        });
      }
      if (counts.notAnswered) {
        base.push({
          id: "notAnswered",
          label: "ikke besvaret",
          legend: "Ikke besvaret",
          color: props.context === "results" ? colors.gray.sim10() : colors.gray.sim00(),
          offset: (base.at(-1)?.offset ?? 0) + (base.at(-1)?.size ?? 0),
          count: counts.notAnswered,
          size: counts.notAnswered / total
        });
      }
    } else {
      if (counts.correct) {
        base.push({
          id: "correct",
          label: "løst rigtigt",
          legend: "Rigtig",
          icon: /*#__PURE__*/jsx(RoundedCheckmarkIcon, {}),
          color: colors.success(),
          offset: 0,
          count: counts.correct,
          size: counts.correct / total
        });
      }
      if (counts.partial) {
        base.push({
          id: "partial",
          label: "løst delvist rigtigt",
          legend: "Delvis rigtig",
          icon: /*#__PURE__*/jsx(PartialCheckmarkIcon, {
            style: {
              marginTop: "0.5px",
              marginLeft: "0.5px"
            }
          }),
          color: colors.warning(),
          offset: (base.at(-1)?.offset ?? 0) + (base.at(-1)?.size ?? 0),
          count: counts.partial,
          size: counts.partial / total
        });
      }
      if (counts.notAnswered) {
        base.push({
          id: "notAnswered",
          label: "ikke udfyldt",
          legend: "Ikke udfyldt",
          color: props.context === "results" ? colors.gray.sim10() : colors.gray.sim00(),
          offset: (base.at(-1)?.offset ?? 0) + (base.at(-1)?.size ?? 0),
          count: counts.notAnswered,
          size: counts.notAnswered / total
        });
      }
      if (counts.wrong) {
        base.push({
          id: "wrong",
          label: "løst forkert",
          legend: "Forkert",
          icon: /*#__PURE__*/jsx(CrossIcon, {
            strokeWidth: 1.5,
            height: 10
          }),
          color: colors.danger(),
          offset: (base.at(-1)?.offset ?? 0) + (base.at(-1)?.size ?? 0),
          count: counts.wrong,
          size: counts.wrong / total
        });
      }
    }
    return base;
  })();
  const gradient = segments.map(segment => `${segment.color} ${(segment.offset * 100).toFixed(6)}%, ${segment.color} ${((segment.offset + segment.size) * 100).toFixed(6)}%`).join(", ");
  const textualLabel = segments.map(segment => `${segment.count} opgave${segment.count !== 1 ? "r" : ""} er ${segment.label}`).join(", ");
  const countLabel = showResult ? `${counts.correct}/${total}` : `${total - counts.notAnswered}/${total}`;
  return /*#__PURE__*/jsxs("div", {
    "aria-label": textualLabel,
    className: wrapper,
    "data-context": props.context,
    children: [/*#__PURE__*/jsxs("div", {
      className: cx(chart, props.className),
      style: tokens.gradient.__override(gradient || undefined),
      children: [segments.map(segment => /*#__PURE__*/jsxs(Fragment, {
        children: [segment.icon && /*#__PURE__*/jsx("span", {
          className: icon,
          style: tokens.__override({
            offset: segment.offset + segment.size / 2,
            pointX: getPointOnCircle(segment.offset + segment.size / 2).x.toFixed(6),
            pointY: getPointOnCircle(segment.offset + segment.size / 2).y.toFixed(6)
          }),
          children: segment.icon
        }), /*#__PURE__*/jsx("span", {
          className: separator,
          style: tokens.__override({
            offset: segment.offset,
            pointX: getPointOnCircle(segment.offset).x.toFixed(6),
            pointY: getPointOnCircle(segment.offset).y.toFixed(6)
          })
        })]
      }, segment.id)), /*#__PURE__*/jsxs("div", {
        className: cx(text, countLabel.length >= 6 && "wide"),
        children: [/*#__PURE__*/jsx("b", {
          children: countLabel
        }), /*#__PURE__*/jsx("span", {
          children: showResult ? "opgaver er løst rigtigt" : "opgaver besvaret"
        })]
      })]
    }), /*#__PURE__*/jsx(PieChartLegend, {
      withAnswered: !showResult,
      withNotAnswered: true,
      withCorrect: showResult,
      withIncorrect: showResult,
      withPartial: showResult,
      context: props.context
    })]
  });
}

export { ResultsPieChart };
//# sourceMappingURL=_pie-chart.js.map
